import { Col, Row } from "react-bootstrap";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";

import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import { GenderInitialValues } from "@/data/WebDataManagement/MasterData/Genders/initial-values";
import GenderSchema from "@/schema/WebDataManagement/MasterData/Genders/GenderSchema";
import ImageInput from "@/components/ImageInput/Index";
import LocalizedInput from "@/components/LocalizedInput";
import RenderToast from "@/components/RenderToast";
import { gendersMasterDataApi } from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { useSelector } from "react-redux";

const GenderModal = forwardRef((_, ref) => {
  const locales = useSelector(
    (state) => state?.siteConfigurationReducer?.locales
  );

  const localeCodes = useMemo(() => {
    return locales ? locales.map((locale) => locale.code) : [];
  }, [locales]);

  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${gendersMasterDataApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Gender ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${gendersMasterDataApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: gendersMasterDataApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: GenderInitialValues(
      localeCodes,
      modalState.id ? showData : null
    ),
    validationSchema: GenderSchema(localeCodes, !!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) formData.append("_method", "PUT");
    if (values.icon) formData.append("icon", values.icon);
    formData.append("code", values.code);
    Object.keys(values.localizations).forEach((key) => {
      if (values.localizations[key]) {
        formData.append(`localizations[${key}]`, values.localizations[key]);
      }
    });

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Gender" : "Add Gender"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"code"}
            saveError={saveError}
            label="ID"
          />
        </Col>
      </Row>

      <LocalizedInput
        formik={formik}
        error={saveError}
        isEdit={!!modalState.id}
      />

      <Row>
        <Col md={12}>
          <ImageInput
            id="icon"
            formFormik={formik}
            fieldName="icon"
            label="Icon"
            error={saveError}
            existingImageUrl={modalState.id && showData?.data?.icon_url}
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default GenderModal;
